import type { Lambda } from 'mobx';
import { action, computed, observable, observe, reaction, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';

import { bind } from 'bind-decorator';
import type { DraftStore } from 'bz-draft';
import type DraftRenderedControlProps from 'bz-draft/modules/draft/entities/control/proptypes';
import jexl from 'jexl';
import Moment from 'moment';
import React, { Component } from 'react';
import type { ScoresGoalsSettingsResponse } from 'tm3-core/api.dtos';
import type { ClinicalBaseControlProps } from '../clinical-base-control';

// DOCS: https://github.com/TechnologyAdvice/Jexl

interface LinkedFormulaValue {
  value: number | null;
  scoreEntryId: number | null;
}

type FormulaValue = LinkedFormulaValue | number | string | null;

interface IProps {
  scoreGoalLink?: ScoresGoalsSettingsResponse;
  formulaVariables: Record<string, string | number>;
  value: FormulaValue;
  formula: string;
  onChange(value: any): void;
}

@observer
export default class Formula extends Component<IProps> {
  disposer?: Lambda;
  @observable formulaVariables: Record<string, string | number>;
  @observable value: FormulaValue = null;

  constructor(props: IProps) {
    super(props);
    this.formulaVariables = this.props.formulaVariables;
    this.value = this.makeInitialValue(this.props.value);

    this.addTransforms();

    this.observeFormulaVariables();
  }

  UNSAFE_componentWillReceiveProps(newProps: IProps) {
    this.value = this.makeInitialValue(newProps.value);
  }

  componentWillUnmount() {
    this.disposer?.();
  }

  componentDidUpdate(prevProps: Readonly<IProps>, prevState: Readonly<{}>, snapshot?: any): void {
    if (this.props.formulaVariables != prevProps.formulaVariables) {
      this.observeFormulaVariables();
    }
  }

  observeFormulaVariables() {
    this.disposer?.();

    this.disposer = reaction(() => this.props.formulaVariables, this.calculateFormula, {
      fireImmediately: true
    });
  }

  makeInitialValue(value: FormulaValue) {
    if (
      this.props.value &&
      typeof this.props.value === 'string' &&
      this.props.value.indexOf('{') >= 0
    ) {
      return JSON.parse(this.props.value) as FormulaValue;
    }
    return value;
  }

  sum(args: Array<number>) {
    args.reduce((accumulator, currentValue) => accumulator + currentValue);
  }

  fieldIsLinkedToScore() {
    return !!this.props.scoreGoalLink && !!this.props.scoreGoalLink.id;
  }

  getNumericValue(val: FormulaValue) {
    if (this.valueIsScoreValue(val)) {
      return val.value;
    }
    return val;
  }

  getLinkedScoreId(): number | null {
    if (this.valueIsScoreValue(this.value)) {
      return this.value.scoreEntryId;
    }
    return null;
  }

  valueIsScoreValue(val: FormulaValue): val is LinkedFormulaValue {
    return !!val && typeof val === 'object' && val.hasOwnProperty('value');
  }

  addTransforms() {
    // NOTE: any transforms here must be added to reservedFormulaKeys on draftStore to prevent their use as variable names

    let sum = (args: Array<number>) =>
      args.reduce((accumulator, currentValue) => accumulator + currentValue);

    jexl.addTransform('sum', function (args: Array<number>) {
      return sum(args);
    });
    jexl.addTransform('avg', function (args: Array<number>) {
      return sum(args) / args.length;
    });

    jexl.addTransform('txtavg', function (args: Array<TxtAvgObj>) {
      const values = args.filter((x) => !!x.field && x.field.length > 0).map((x) => x.value);
      return values.length > 0 ? sum(values) / values.length : null;
    });

    jexl.addTransform('datediff', (d1, d2, period) => {
      let mD1 = Moment(d1, 'DD/MM/YYYY');
      let mD2 = Moment(d2).isValid() ? Moment(d2) : Moment(d2, 'DD/MM/YYYY');

      if (mD1.isValid() && mD2.isValid()) {
        if (period === 'year') {
          mD1 = mD1.startOf('day');
          mD2 = mD2.startOf('day');
        }
        return mD1.diff(mD2, period);
      }

      return 'Invalid Date';
    });

    jexl.addTransform('checktotal', (value, isSelected) => {
      return value === null ? 0 : isSelected === false ? 0 : value;
    });
  }

  @action.bound
  async calculateFormula() {
    let props = this.props;
    let current = this.value;
    let formula = props.formula;

    if (this) {
      formula = this.props.formula;
    }

    let res = await jexl.eval(formula, this.props.formulaVariables);
    const isBoolean = res === true || res === false;
    const isNumber = !isNaN(res) && !isBoolean;
    const displayValue = isNumber ? +(Math.round(res * 100) / 100).toFixed(2) : res;
    const isFailedNumber = isNaN(res) && !isBoolean;

    if (this.getNumericValue(current) !== displayValue && (isNumber || isBoolean)) {
      this.props.onChange?.(this.makeValue(displayValue));
    } else if (isFailedNumber) {
      this.props.onChange?.(res);
    }
  }

  makeValue(val: number | string | boolean) {
    if (this.fieldIsLinkedToScore() && this.valueIsScoreValue(this.value)) {
      return { value: val, scoreEntryId: this.getLinkedScoreId() };
    } else {
      return val;
    }
  }

  render() {
    return <span>{this.getNumericValue(this.value)?.toString()}</span>;
  }
}

class TxtAvgObj {
  field?: string;
  value?: number;
}
